<template>
  <Teleport to="body">
<div class="modal fade" tabindex="-1" :id="modalId" ref="modalEl" aria-labelledby="odontogram-modal-title">
    <div class="modal-dialog modal-dialog-centered">
      <form method="post" @submit.prevent="submit">
        <div class="modal-content">

          <div class="modal-header">
            <h5 class="modal-title" id="odontogram-modal-title">
              Dente {{ tooth }}
              <span class="text-muted fw-normal fs-7 ms-2">{{ quadrant }}</span>
            </h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Fechar">
              <i class="bi bi-x-lg"></i>
            </div>
          </div>

          <div class="modal-body">

            <div class="mb-6">
              <label class="fs-6 fw-bold form-label">Condição do dente</label>
              <div class="d-flex flex-wrap gap-2">
                <button v-for="(status, key) in toothStatusOptions"
                        :key="key"
                        type="button"
                        ref="firstField"
                        class="btn btn-sm"
                        :class="form.toothStatus === key ? 'btn-primary' : 'btn-light'"
                        @click="form.toothStatus = key">
                  {{ status.label }}
                </button>
              </div>
            </div>

            <template v-if="form.toothStatus === 'present'">
              <div class="mb-6">
                <label class="fs-6 fw-bold form-label" :for="modalId + '-face'">Face</label>
                <select class="form-select form-select-solid" :id="modalId + '-face'" v-model="form.face">
                  <option value="">Nenhuma (dente inteiro)</option>
                  <option v-for="code in faceOptions" :key="code" :value="code">
                    {{ code }} — {{ faceLabels[code] }}
                  </option>
                </select>
              </div>

              <div class="mb-6" v-if="form.face">
                <label class="fs-6 fw-bold form-label">Condição da face {{ form.face }}</label>
                <div class="d-flex flex-wrap gap-2">
                  <button v-for="(status, key) in faceStatusOptions"
                          :key="key"
                          type="button"
                          class="btn btn-sm"
                          :class="form.faceStatus === key ? 'btn-primary' : 'btn-light'"
                          @click="form.faceStatus = key">
                    {{ status.label }}
                  </button>
                </div>
              </div>
            </template>

            <div class="separator my-5"></div>

            <div class="mb-4" v-if="form.toothStatus !== 'absent'">
              <label class="fs-6 fw-bold form-label">Endodontia</label>
              <div class="d-flex flex-wrap gap-2 mb-3">
                <button v-for="(status, key) in endoStatusOptions"
                        :key="key"
                        type="button"
                        class="btn btn-sm"
                        :class="form.endoStatus === key ? 'btn-primary' : 'btn-light'"
                        @click="setEndoStatus(key)">
                  {{ status.label }}
                </button>
              </div>
              <div class="mb-3" v-if="form.endoStatus !== 'none'">
                <label class="fs-6 fw-bold form-label" :for="modalId + '-endo-obs'">Observação (canal)</label>
                <input type="text"
                       class="form-control form-control-solid"
                       :id="modalId + '-endo-obs'"
                       v-model="form.endoObs"
                       placeholder="Ex.: Retorno em 7 dias">
              </div>
              <button v-if="form.endoStatus !== 'none'"
                      type="button"
                      class="btn btn-sm btn-light-primary"
                      @click="fillCanalProcedure">
                Incluir procedimento de canal
              </button>
            </div>

            <div class="separator my-5" v-if="form.toothStatus !== 'absent'"></div>

            <div class="mb-4">
              <label class="fs-6 fw-bold form-label" :for="modalId + '-proc'">
                Procedimento <span class="text-muted fw-normal">(opcional)</span>
              </label>
              <input type="text"
                     class="form-control form-control-solid"
                     :id="modalId + '-proc'"
                     v-model="form.procedureName"
                     placeholder="Ex.: Restauração em resina composta">
            </div>

            <div class="row" v-if="form.procedureName">
              <div class="col-md-6 mb-4">
                <label class="fs-6 fw-bold form-label" :for="modalId + '-proc-status'">Situação</label>
                <select class="form-select form-select-solid" :id="modalId + '-proc-status'" v-model="form.procedureStatus">
                  <option value="planned">Planejado</option>
                  <option value="in_progress">Em andamento</option>
                  <option value="done">Realizado</option>
                </select>
              </div>
              <div class="col-md-6 mb-4">
                <label class="fs-6 fw-bold form-label" :for="modalId + '-proc-obs'">Observação</label>
                <input type="text"
                       class="form-control form-control-solid"
                       :id="modalId + '-proc-obs'"
                       v-model="form.procedureObs">
              </div>
            </div>

            <div v-if="procedures.length" class="mt-4">
              <label class="fs-6 fw-bold form-label">Já lançados neste dente</label>
              <div v-for="procedure in procedures"
                   :key="procedure.uid"
                   class="d-flex justify-content-between align-items-center gap-3 py-2 border-bottom">
                <div class="fs-7">
                  {{ procedure.name }}
                  <span class="text-muted" v-if="procedure.faces && procedure.faces.length">
                    · {{ procedure.faces.join(', ') }}
                  </span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span class="badge" :class="badgeClass(procedure.status)">{{ statusLabel(procedure.status) }}</span>
                  <button type="button"
                          class="btn btn-icon btn-sm btn-active-light-danger"
                          :aria-label="'Remover ' + procedure.name"
                          @click="$emit('remove', procedure.uid)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>

          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
            <button type="submit" class="btn btn-primary">Salvar</button>
          </div>

        </div>
      </form>
    </div>
  </div>
  </Teleport>
</template>

<script>
import {
  ENDO_STATUS,
  ENDO_TO_PROCEDURE_STATUS,
  FACE_LABELS,
  FACE_STATUS,
  TOOTH_STATUS,
  faceCodes,
  quadrantLabel,
} from './odontogram-layout';

export default {
  name: 'OdontogramToothModal',
  emits: ['save', 'remove', 'closed'],
  props: {
    modalId: { type: String, required: true },
    tooth: { type: String, default: '' },
    face: { type: String, default: null },
    toothState: { type: Object, default: null },
    procedures: { type: Array, default: () => [] },
  },
  data() {
    return {
      faceLabels: FACE_LABELS,
      toothStatusOptions: TOOTH_STATUS,
      faceStatusOptions: FACE_STATUS,
      endoStatusOptions: ENDO_STATUS,
      form: this.emptyForm(),
    };
  },
  computed: {
    quadrant() {
      return this.tooth ? quadrantLabel(this.tooth) : '';
    },
    faceOptions() {
      if (!this.tooth) {
        return [];
      }

      const arch = [1, 2, 5, 6].includes(Number(this.tooth[0])) ? 'upper' : 'lower';
      const codes = faceCodes(this.tooth, arch);

      return [codes.top, codes.right, codes.center, codes.left, codes.bottom];
    },
  },
  mounted() {
    // Devolve o foco ao dente quando o modal fecha: sem isso o teclado volta
    // para o topo da pagina e o dentista perde o lugar.
    this.$refs.modalEl.addEventListener('hidden.bs.modal', () => this.$emit('closed'));
    this.$refs.modalEl.addEventListener('shown.bs.modal', () => {
      const first = this.$refs.firstField;
      const el = Array.isArray(first) ? first[0] : first;
      if (el) {
        el.focus();
      }
    });
  },
  methods: {
    emptyForm() {
      return {
        toothStatus: 'present',
        face: '',
        faceStatus: 'healthy',
        endoStatus: 'none',
        endoObs: '',
        procedureName: '',
        procedureStatus: 'planned',
        procedureObs: '',
      };
    },

    /** Chamado pelo pai antes de abrir o modal. */
    load() {
      const state = this.toothState ?? {};
      const faces = state.faces ?? {};
      const face = this.face ?? '';
      const endo = state.endo ?? {};

      this.form = {
        ...this.emptyForm(),
        toothStatus: state.status ?? 'present',
        face,
        faceStatus: (face && faces[face]) || 'healthy',
        endoStatus: endo.status && ENDO_STATUS[endo.status] ? endo.status : 'none',
        endoObs: endo.obs ?? '',
      };
    },

    setEndoStatus(key) {
      this.form.endoStatus = key;
      if (key === 'none') {
        this.form.endoObs = '';
      }
    },

    fillCanalProcedure() {
      if (!this.form.procedureName.trim()) {
        this.form.procedureName = 'Tratamento endodôntico';
      }
      this.form.procedureStatus = ENDO_TO_PROCEDURE_STATUS[this.form.endoStatus] ?? 'planned';
      if (!this.form.procedureObs.trim() && this.form.endoObs.trim()) {
        this.form.procedureObs = this.form.endoObs.trim();
      }
    },

    statusLabel(status) {
      return { planned: 'Planejado', in_progress: 'Em andamento', done: 'Realizado' }[status] ?? status;
    },

    badgeClass(status) {
      return status === 'done' ? 'badge-light-success' : 'badge-light-warning';
    },

    submit() {
      const payload = {
        toothStatus: this.form.toothStatus,
        faceStatus: null,
        endo: null,
        procedure: null,
      };

      if (this.form.toothStatus === 'present' && this.form.face) {
        payload.faceStatus = { face: this.form.face, status: this.form.faceStatus };
      }

      if (this.form.toothStatus !== 'absent' && this.form.endoStatus !== 'none') {
        payload.endo = {
          status: this.form.endoStatus,
          obs: this.form.endoObs.trim(),
        };
      }

      if (this.form.procedureName.trim()) {
        payload.procedure = {
          name: this.form.procedureName.trim(),
          faces: this.form.face ? [this.form.face] : [],
          status: this.form.procedureStatus,
          obs: this.form.procedureObs.trim(),
        };
      }

      this.$emit('save', payload);
    },
  },
};
</script>
